import { useContext } from "react";
import { useRequest } from "./request.hooks";
import { useQuery } from "@tanstack/react-query";
import { UserStateContext } from "@/contexts/UserStateProvider";

export const EXPENSE_ITEM_LIST = "expenseItemList";

interface ExpenseItem {
    id: number;
    label: string;
    description?: string;
    category: string;
    amount: number;
    currency: string;
    type: string;
    date: Date;
}

interface ExpenseItemRecord {
    id: number;
    expenseItemId: number;
    amount: number;
    currency: string;
    date: Date;
    expenseItem: ExpenseItem;
}

interface ExpenseItemListResponse {
    items: ExpenseItemRecord[];
    totalCount: number;
    currentPage: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
}

interface LogItemResponse {
    success: boolean;
    message: string;
    expenseItem: ExpenseItem;
}

export const useExpenseItemList = (
    page: number = 1,
    pageSize: number = 10,
    search?: string
) => {
    const { request } = useRequest();
    const { token } = useContext(UserStateContext);
    const { isLoading, isFetched, error, data } = useQuery<ExpenseItemListResponse>({
        queryKey: [EXPENSE_ITEM_LIST, token, page, pageSize, search],
        queryFn: async () => {
            const response = await request({
                url: "/api/v1/expenseItem/retrieve/list",
                method: "GET",
                params: {
                    page: page.toString(),
                    pageSize: pageSize.toString(),
                    search: search || ""
                }
            });
            
            if (response.success) {
                return response.content;
            } else {
                throw new Error(response.errorMessage || "Failed to fetch expense items");
            }
        },
        staleTime: 10 * 60 * 1000, // 10 minutes in milliseconds
    });

    return {
        expenseItems: data,
        isLoading,
        isFetched,
        error
    };
};

export const useExpenseItemService = () => {
    const { request } = useRequest();

    const logItem = async (
        label: string,
        description: string | undefined,
        category: string,
        amount: number,
        currency: string,
        type: string,
        date: string
    ): Promise<any> => {
        return request({
            url: `/api/v1/expenseItem/actions/log`,
            method: "POST",
            headers: {
                "content-type": "application/json",
            },
            data: { label, description, category, amount, currency, type, date }
        });
    };


    const deleteItem = async (expenseItemRecordId: number): Promise<any> => {
        return request({
            url: `/api/v1/expenseItem/actions/delete`,
            method: "POST",
            headers: {
                "content-type": "application/json",
            },
            data: { expenseItemRecordId }
        });
    };

    return {
        logItem,
        deleteItem
    };
};
